import React from "react";
import { Box, ButtonBase, Typography } from "@mui/material";
import { STATUS_STYLES, FONT_DISPLAY } from "./statusStyles";

const TILE_ORDER = ["active", "expiring", "expired", "missing"];

/**
 * Four status tiles. Each is a toggle button: clicking one narrows the timeline to that status, clicking it again clears it.
 * The Missing tile is omitted when the tenant defines no required certification types.
 */
export default function StatusTiles({ counts, selected, onToggle, showMissing }) {
  const tiles = TILE_ORDER.filter((id) => id !== "missing" || showMissing);
  return (
    <Box
      role="group"
      aria-label="Filter certifications by status"
      sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", md: `repeat(${tiles.length}, 1fr)` }, gap: 1.5, mb: 4.5 }}
    >
      {tiles.map((id) => {
        const style = STATUS_STYLES[id];
        const pressed = selected === id;
        return (
          <ButtonBase
            key={id}
            data-testid={`cert-tile-${id}`}
            aria-pressed={pressed}
            onClick={() => onToggle(id)}
            sx={{
              display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1.25, textAlign: "left", py: 2, px: 2.25, borderRadius: "12px", bgcolor: pressed ? "var(--color-surface-press-stronger, #efefef)" : "background.paper",
              border: "1px solid", borderColor: pressed ? "var(--color-primary, #150f23)" : "var(--color-hairline-cloud, #e5e7eb)",
              transition: "border-color .15s, box-shadow .15s",
              "&:focus-visible": { outline: "2px solid var(--ring-focus, rgba(59,130,246,.6))", outlineOffset: 2 },
              "@media (prefers-reduced-motion: reduce)": { transition: "none" },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box aria-hidden sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: style.bar }} />
              <Typography component="span" sx={{ fontSize: 11.5, fontWeight: 600, letterSpacing: ".02em", textTransform: "uppercase", color: "text.secondary" }}>
                {style.tile}
              </Typography>
            </Box>
            <Typography component="span" data-testid={`cert-tile-${id}-count`} sx={{ display: "block", fontFamily: FONT_DISPLAY, fontSize: 36, fontWeight: 700, lineHeight: 1, letterSpacing: "-.02em", color: "text.primary", fontVariantNumeric: "tabular-nums" }}>
              {counts[id]}
            </Typography>
            <Typography component="span" sx={{ fontSize: 12.5, color: "var(--color-accent-violet-mid, #6b7280)" }}>{style.note}</Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );
}
